import type { UseDragParams, UseDragReturn } from './index.types';
import * as React from 'react';

/**
 * Horizontal pointer-drag resize: captures pointer, updates width live, and
 * persists on pointer up/cancel.
 */
export const useDrag = ({
  disabled = false,
  handleSide,
  widthRef,
  setWidth,
  persistWidth,
}: UseDragParams): UseDragReturn => {
  const [isDragging, setIsDragging] = React.useState(false);
  const isDraggingRef = React.useRef(false);
  const dragStartXRef = React.useRef(0);
  const dragStartWidthRef = React.useRef(0);

  const endDrag = React.useCallback(
    (target: HTMLDivElement, pointerId: number) => {
      if (!isDraggingRef.current) {
        return;
      }

      if (target.hasPointerCapture(pointerId)) {
        target.releasePointerCapture(pointerId);
      }

      isDraggingRef.current = false;
      setIsDragging(false);
      persistWidth();
    },
    [persistWidth]
  );

  const handlePointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (disabled || event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragStartXRef.current = event.clientX;
      dragStartWidthRef.current = widthRef.current;
      isDraggingRef.current = true;
      setIsDragging(true);
    },
    [disabled, widthRef]
  );

  const handlePointerMove = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) {
        return;
      }

      const deltaX = event.clientX - dragStartXRef.current;
      const signedDelta = handleSide === 'right' ? deltaX : -deltaX;
      setWidth(dragStartWidthRef.current + signedDelta);
    },
    [handleSide, setWidth]
  );

  const handlePointerUp = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      endDrag(event.currentTarget, event.pointerId);
    },
    [endDrag]
  );

  return {
    isDragging,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  };
};
